import React, { useState, useEffect } from 'react';
import './Playground.css';
import { loadConfig, storeQueue, loadQueue } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import ReadyReports from '../components/Playground/ReadyReports';
import SectionsSelector from '../components/Playground/SectionsSelector';
import GenerateButton from '../components/Playground/GenerateButton';
import ReportModal from '../components/Reports/ReportModal';

function Playground() {
  const [config, setConfig] = useState(null);
  const [selectedSections, setSelectedSections] = useState([]);
  const [sectionSettings, setSectionSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [message, setMessage] = useState('');
  const [queueReports, setQueueReports] = useState([]);
  const [loadingReports, setLoadingReports] = useState(false);
  const [modalReport, setModalReport] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { user } = useAuth();

  // Generate default title from selected sections
  const generateDefaultTitle = (sections) => {
    if (!sections || sections.length === 0) return '';
    
    // Convert section IDs to readable names
    const readableNames = sections.map(section => {
      // Convert camelCase or snake_case to Title Case
      return section
        .replace(/([A-Z])/g, ' $1') // Add space before capital letters
        .replace(/_/g, ' ') // Replace underscores with spaces
        .replace(/^./, str => str.toUpperCase()) // Capitalize first letter
        .trim();
    });
    
    return readableNames.join(', ');
  };

  useEffect(() => {
    loadConfiguration();
  }, []);

  useEffect(() => {
    if (user) {
      loadQueueReports();
    }
  }, [user]);

  // Remove loadSavedEmail function - no longer needed

  const loadConfiguration = async () => {
    try {
      setLoading(true);
      const config = await loadConfig();

      // Pass the full config object now (includes sections and categories)
      setConfig(config);
    } catch (error) {
      console.error('Error loading config:', error);
      setMessage('Error loading configuration. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadQueueReports = async () => {
    try {
      setLoadingReports(true);
      const reports = await loadQueue();
      setQueueReports(reports);
    } catch (error) {
      console.error('Error loading queue reports:', error);
      // Don't show error message for this as it's not critical
    } finally {
      setLoadingReports(false);
    }
  };

  // Remove email handling functions - no longer needed

  const handleSectionToggle = (sectionKey) => {
    setSelectedSections(prev => {
      if (prev.includes(sectionKey)) {
        return prev.filter(section => section !== sectionKey);
      } else {
        if (prev.length >= 6) {
          setMessage('You can only select up to 6 sections.');
          return prev;
        }
        return [...prev, sectionKey];
      }
    });
    setMessage('');
  };

  const handleSettingChange = (sectionKey, settingKey, value) => {
    setSectionSettings(prev => ({
      ...prev,
      [sectionKey]: {
        ...prev[sectionKey],
        [settingKey]: value
      }
    }));
  };

  const handleGenerate = async (title) => {
    if (selectedSections.length === 0) {
      setMessage('Please select at least one section.');
      return;
    }

    if (!title || !title.trim()) {
      setMessage('Please enter a newsletter title.');
      return;
    }

    try {
      setGenerating(true);
      setMessage('');

      // Create settings object with selected sections and their settings
      const settings = {};
      selectedSections.forEach(section => {
        settings[section] = sectionSettings[section] || {};
      });

      await storeQueue(title.trim(), settings);
      setMessage('Settings stored successfully! Your newsletter is being generated. Check the "Reports Queue" section below.');
      
      // Clear selections after successful generation
      setSelectedSections([]);
      
      // Refresh queue reports after a short delay
      setTimeout(() => {
        loadQueueReports();
      }, 2000);
    } catch (error) {
      console.error('Error storing settings:', error);
      setMessage('Error generating newsletter. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  const handleReportClick = (report) => {
    setModalReport(report);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setModalReport(null);
  };

  const handleReportDeleted = (deletedReportId) => {
    // Remove the deleted report from the queue
    setQueueReports(prev => prev.filter(report => report.id !== deletedReportId));
  };

  if (loading) {
    return (
      <div className="playground loading">
        <h1>Playground</h1>
        <p>Loading configuration...</p>
      </div>
    );
  }

  return (
    <div className="playground">
      {message && (
        <div className={`message ${message.includes('Error') ? 'error' : 'success'}`}>
          {message}
        </div>
      )}

      {/* Left Column - Configuration */}
      <div className="playground-left">


        <SectionsSelector
          config={config}
          selectedSections={selectedSections}
          onSectionToggle={handleSectionToggle}
          sectionSettings={sectionSettings}
          onSettingChange={handleSettingChange}
        />

        <div className="generation-section component-card">
          <GenerateButton
            generating={generating}
            disabled={generating || selectedSections.length === 0}
            onClick={handleGenerate}
            defaultTitle={generateDefaultTitle(selectedSections)}
          />
        </div>
      </div>

      {/* Right Column - Results and Queue */}
      <div className="playground-right">
        <ReadyReports
          loadingReports={loadingReports}
          queueReports={queueReports}
          onReportClick={handleReportClick}
          onReportDeleted={handleReportDeleted}
        />
      </div>

      {/* Report Modal */}
      <ReportModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        report={modalReport}
        reportType="on_demand"
      />
    </div>
  );
}

export default Playground;
